import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Alert,
  LinearProgress,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Container,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Checkbox,
  CircularProgress,
  Skeleton,
  Snackbar
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Preview as PreviewIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  PlayArrow as PlayIcon,
  Image as ImageIcon,
  PhotoLibrary as PhotoLibraryIcon,
  CloudDownload as CloudDownloadIcon,
  Folder as FolderIcon,
  ArrowBack as ArrowBackIcon,
  NotificationImportant as NotificationIcon,
  Send as SendIcon,
  Edit as EditIcon,
  AccountTree as GraphIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

// Helper function to check if file is HEIC (checks both name and extension)
const isHeicFile = (fileName, fileExt = null) => {
  // Check explicit extension first (from Contact Cloud API details.extension)
  if (fileExt) {
    const ext = fileExt.toLowerCase().replace(/^\./, '');
    return ext === 'heic' || ext === 'heif';
  }
  // Fallback to checking filename
  if (!fileName) return false;
  const ext = fileName.toLowerCase().split('.').pop();
  return ext === 'heic' || ext === 'heif';
};

// Helper function to convert HEIC to JPG
const convertHeicToJpg = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        // Get file as array buffer and create blob
        const blob = new Blob([e.target.result], { type: file.type });

        // For now, we'll use Canvas API as a fallback
        // In a real scenario, you might want to use a library like heic2any
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);

          canvas.toBlob((jpgBlob) => {
            const jpgFile = new File([jpgBlob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), {
              type: 'image/jpeg',
              lastModified: file.lastModified
            });
            resolve(jpgFile);
          }, 'image/jpeg', 0.9);
        };
        img.onerror = () => {
          // If canvas conversion fails, fall back to original
          console.warn('Canvas HEIC conversion failed, using original');
          resolve(file);
        };
        img.src = URL.createObjectURL(blob);
      } catch (err) {
        console.warn('HEIC conversion error:', err);
        resolve(file);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
};

// Helper function to convert HEIC file to preview data URL
const convertHeicToPreviewUrl = async (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const blob = new Blob([e.target.result], { type: file.type });
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
          resolve(dataUrl);
        };
        img.onerror = () => {
          console.warn('HEIC preview conversion failed');
          resolve(null);
        };
        img.src = URL.createObjectURL(blob);
      } catch (err) {
        console.warn('HEIC preview error:', err);
        resolve(null);
      }
    };
    reader.onerror = () => resolve(null);
    reader.readAsArrayBuffer(file);
  });
};

const MediaManager = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const { selectedBusiness } = useBusiness();
  const [mediaData, setMediaData] = useState(null);
  const [metadataMap, setMetadataMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const previousLocationIdRef = useRef(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 12;

  // Filtering and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [filteredMedia, setFilteredMedia] = useState([]);

  // UI state
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [previewDialog, setPreviewDialog] = useState({ open: false, media: null });
  const [uploadDialog, setUploadDialog] = useState({
    open: false,
    files: [],
    sourceUrl: '',
    uploading: false,
    category: 'ADDITIONAL',
    method: 'file',  // 'file', 'url', or 's3'
    s3Files: [],
    s3Loading: false,
    selectedS3Files: [],  // Array for multiple selection
    s3CurrentFolder: null,
    s3FolderStack: [],
    s3PreviewFile: null  // For image preview
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, media: null, deleting: false });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [businessData, setBusinessData] = useState(null);
  const [ownerNotificationOpen, setOwnerNotificationOpen] = useState(false);
  const [ownerNotificationRecipient, setOwnerNotificationRecipient] = useState('d.nawaz@am-beratung.de');
  const [ownerNotificationSending, setOwnerNotificationSending] = useState(false);
  const [ownerNotificationEmailContent, setOwnerNotificationEmailContent] = useState('');
  const [tenantId, setTenantId] = useState(null);
  const [folderStructureOpen, setFolderStructureOpen] = useState(false);
  const [folderStructure, setFolderStructure] = useState(null);

  // Fetch media data
  const fetchMediaData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log(`🔄 Fetching media for location: ${locationId}`);
      const response = await fetch(`/api/media/${locationId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ Media data fetched successfully');
      console.log(`📊 Total media items: ${data.data?.mediaItems?.length || 0}`);
      if (data.data?.mediaItems && data.data.mediaItems.length > 0) {
        const firstItem = data.data.mediaItems[0];
        console.log(`📸 First media item details:`, {
          name: firstItem.name?.split('/').pop(),
          format: firstItem.mediaFormat,
          hasThumbnail: !!firstItem.thumbnailUrl,
          hasProxyThumbnail: !!firstItem.proxyThumbnailUrl,
          hasSourceUrl: !!firstItem.sourceUrl,
          hasProxySourceUrl: !!firstItem.proxySourceUrl,
          thumbnailUrl: firstItem.thumbnailUrl,
          proxyThumbnailUrl: firstItem.proxyThumbnailUrl,
          sourceUrl: firstItem.sourceUrl
        });
      }
      setMediaData(data.data || {});
    } catch (err) {
      console.error('❌ Failed to fetch media:', err);
      setError(`Fehler beim Laden der Medien: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessData = async () => {
    if (locationId) {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`/api/businesses/${locationId}/details`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          setBusinessData(data);
          // Extract tenantId from Contact Cloud Tenant ID (not locationId)
          if (data.contactCloudTenantId) {
            setTenantId(data.contactCloudTenantId);
            setFolderStructure(data.mediaMapping);
          }
        }
      } catch (err) {
        console.error('Error fetching business data:', err);
      }
    }
  };

  const fetchFreshFolderStructure = async () => {
    if (locationId) {
      try {
        console.log('🔄 Fetching fresh folder structure...');
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`/api/businesses/${locationId}/details`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.mediaMapping?.locationId) {
            setFolderStructure(data.mediaMapping);
            console.log('✅ Fresh folder structure loaded');
          }
        }
      } catch (err) {
        console.error('Error fetching fresh folder structure:', err);
      }
    }
  };

  // Fetch metadata map from database
  const fetchMetadataMap = async () => {
    if (locationId) {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`/api/folders-with-metadata/${locationId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          console.log(`✅ Fetched metadata map with ${data.totalImages} images`);
          setMetadataMap(data.metadataMap);
        }
      } catch (err) {
        console.error('Error fetching metadata map:', err);
      }
    }
  };

  // Update file metadata in database (status and/or category)
  const updateFileMetadata = async (fileId, updates) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/folders-with-metadata/${locationId}/${fileId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        console.log(`✅ Updated file metadata:`, updates);
        // Update local metadataMap
        setMetadataMap(prev => ({
          ...prev,
          [fileId]: { ...prev?.[fileId], ...updates }
        }));
        setSnackbar({
          open: true,
          message: `Metadaten erfolgreich aktualisiert`,
          severity: 'success'
        });
      } else {
        throw new Error('Failed to update metadata');
      }
    } catch (err) {
      console.error('Error updating file metadata:', err);
      setSnackbar({
        open: true,
        message: `Fehler beim Aktualisieren: ${err.message}`,
        severity: 'error'
      });
    }
  };

  useEffect(() => {
    console.log('📍 Location ID changed:', locationId);
    if (locationId && locationId !== previousLocationIdRef.current) {
      previousLocationIdRef.current = locationId;
      fetchMediaData();
      fetchBusinessData();
      fetchMetadataMap();
    }
  }, [locationId]);

  // Log businessData when it changes
  useEffect(() => {
    if (businessData?.mediaMapping) {
      console.log('✅ businessData loaded with mediaMapping:', {
        folders: businessData.mediaMapping.folders?.length || 0,
        totalImages: businessData.mediaMapping.folders?.reduce((sum, f) => sum + (f.images?.length || 0), 0) || 0,
        folderStructure: businessData.mediaMapping.folders?.map(f => ({
          name: f.folderName,
          imagesCount: f.images?.length || 0,
          firstImage: f.images?.[0]?.name,
          allData: f
        }))
      });
    }
  }, [businessData]);

  // Fetch owner notification email content when dialog opens
  useEffect(() => {
    if (ownerNotificationOpen) {
      getOwnerNotificationEmailContent().then(content => {
        setOwnerNotificationEmailContent(content);
      });
    }
  }, [ownerNotificationOpen]);

  // Filter & sort logic
  useEffect(() => {
    if (!mediaData?.mediaItems) {
      setFilteredMedia([]);
      return;
    }

    let filtered = [...mediaData.mediaItems];

    // Search
    if (searchTerm) {
      filtered = filtered.filter(item =>
        (item.name?.toLowerCase() || '').includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (filterCategory !== 'all') {
      filtered = filtered.filter(item =>
        item.locationAssociation?.category === filterCategory
      );
    }

    // Type filter (PHOTO/VIDEO)
    if (filterType !== 'all') {
      filtered = filtered.filter(item =>
        item.mediaFormat === filterType
      );
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createTime || 0) - new Date(a.createTime || 0);
        case 'oldest':
          return new Date(a.createTime || 0) - new Date(b.createTime || 0);
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        default:
          return 0;
      }
    });

    setFilteredMedia(filtered);
    setCurrentPage(1);
  }, [mediaData, searchTerm, filterCategory, filterType, sortBy]);

  // Pagination
  const paginatedMedia = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return filteredMedia.slice(start, end);
  }, [filteredMedia, currentPage]);

  const totalPages = Math.ceil(filteredMedia.length / ITEMS_PER_PAGE);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Media actions
  const handlePreview = (media) => {
    setPreviewDialog({ open: true, media });
  };

  const handleUploadClick = () => {
    setUploadDialog({ ...uploadDialog, open: true });
  };

  const handleOwnerNotification = () => {
    setOwnerNotificationRecipient(businessData?.email || '');
    setOwnerNotificationOpen(true);
  };

  const getOwnerNotificationEmailContent = async () => {
    let firstName = businessData?.businessName?.split(' ')[0] || 'Unternehmer';

    // Fetch HubSpot contact to get first name using contactCloudTenantId
    if (businessData?.contactCloudTenantId) {
      try {
        const token = localStorage.getItem('auth_token');
        console.log('📞 Searching HubSpot contact with contactcloud_tenant_id:', businessData.contactCloudTenantId);

        const hubspotResponse = await fetch('/api/hubspot/contacts/search', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            contactCloudTenantId: businessData.contactCloudTenantId
          })
        });

        console.log('📞 HubSpot response status:', hubspotResponse.status);
        if (hubspotResponse.ok) {
          const contactResponse = await hubspotResponse.json();
          console.log('📞 HubSpot search response:', contactResponse);

          if (contactResponse.results && contactResponse.results.length > 0) {
            const contact = contactResponse.results[0];
            if (contact.properties?.firstname) {
              firstName = contact.properties.firstname;
              console.log('✅ FirstName found:', firstName);
            } else {
              console.log('⚠️ FirstName property not found in HubSpot contact');
            }
          } else {
            console.log('⚠️ No HubSpot contact found with contactcloud_tenant_id:', businessData.contactCloudTenantId);
          }
        } else {
          console.warn('📞 HubSpot API error:', hubspotResponse.status, hubspotResponse.statusText);
        }
      } catch (error) {
        console.warn('Failed to fetch HubSpot contact firstname:', error);
      }
    } else {
      console.warn('⚠️ No contactCloudTenantId found in businessData');
    }

    return `Hallo ${firstName},

uns ist aufgefallen, dass seit einiger Zeit keine neuen Bilder in deinem Google-Unternehmensprofil veröffentlicht wurden.

Aktuelle Bilder helfen dabei, das Profil lebendig zu halten und Interessenten einen guten und authentischen Eindruck von deinem Unternehmen zu vermitteln.

Sende uns daher gerne neue Bilder zu oder lade sie direkt in deiner A&M Cloud hoch. Sobald passende Bilder verfügbar und freigegeben sind, können wir diese in deinem Google-Unternehmensprofil ergänzen.

Mit freundlichen Grüßen
Das Team der A&M

A&M Unternehmerberatung GmbH
Großer Kolonnenweg 18
30163 Hannover
Sitz der Gesellschaft: Hannover
Registergericht: Amtsgericht Hannover, HRB 216201
Geschäftsführer Alexander Thieme, Marvin Flenche`;
  };

  const handleSendOwnerNotification = async () => {
    if (!ownerNotificationRecipient.trim()) return;

    setOwnerNotificationSending(true);

    try {
      const emailBody = await getOwnerNotificationEmailContent();

      const response = await fetch('/api/send-owner-notification-email', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          businessData: {
            businessName: businessData?.businessName
          },
          review: {
            rating: 0,
            authorName: '',
            time: new Date().toISOString(),
            text: ''
          },
          recipient: ownerNotificationRecipient,
          subject: 'Hinweis zu deinen Google-Bewertungen',
          body: emailBody
        })
      });

      if (response.ok) {
        setSnackbar({ open: true, message: `E-Mail erfolgreich an ${ownerNotificationRecipient} versendet!`, severity: 'success' });
        setOwnerNotificationOpen(false);
      } else {
        throw new Error('E-Mail Versand fehlgeschlagen');
      }
    } catch (error) {
      console.error('Owner notification email error:', error);
      setSnackbar({ open: true, message: `Fehler beim E-Mail Versand: ${error.message}`, severity: 'error' });
    } finally {
      setOwnerNotificationSending(false);
    }
  };

  const fetchS3Files = async (parentId = null) => {
    try {
      setUploadDialog(prev => ({ ...prev, s3Loading: true }));
      let url = `/api/businesses/${locationId}/library`;
      if (parentId) {
        url += `?parent=${parentId}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch library files: ${response.statusText}`);
      }

      const data = await response.json();
      // Extract files from nested response structure
      let files = [];
      if (Array.isArray(data)) {
        files = data;
      } else if (Array.isArray(data.rows?.rows?.documents)) {
        files = data.rows.rows.documents;
      } else if (Array.isArray(data.rows)) {
        files = data.rows;
      } else if (data.documents && Array.isArray(data.documents)) {
        files = data.documents;
      }

      console.log('📚 S3 Library items loaded:', files.length);
      setUploadDialog(prev => ({ ...prev, s3Files: files, s3Loading: false }));
    } catch (err) {
      console.error('❌ Failed to fetch S3 files:', err);
      setSnackbar({
        open: true,
        message: `Fehler beim Laden der Cloud-Dateien: ${err.message}`,
        severity: 'error'
      });
      setUploadDialog(prev => ({ ...prev, s3Loading: false }));
    }
  };

  const handleS3FolderOpen = (folder) => {
    setUploadDialog(prev => ({
      ...prev,
      s3FolderStack: [...prev.s3FolderStack, { id: prev.s3CurrentFolder, name: prev.s3CurrentFolder === null ? 'Bibliothek' : 'Zurück' }],
      s3CurrentFolder: folder._id
    }));
    fetchS3Files(folder._id);
  };

  const handleS3FolderBack = () => {
    setUploadDialog(prev => {
      if (prev.s3FolderStack.length === 0) return prev;
      const newStack = [...prev.s3FolderStack];
      const previousFolder = newStack.pop();
      const newCurrentFolder = previousFolder.id;

      // Fetch files for the previous folder
      setTimeout(() => fetchS3Files(newCurrentFolder), 0);

      return {
        ...prev,
        s3FolderStack: newStack,
        s3CurrentFolder: newCurrentFolder
      };
    });
  };

  const handleS3GoToRoot = () => {
    setUploadDialog(prev => ({
      ...prev,
      s3CurrentFolder: null,
      s3FolderStack: []
    }));
    fetchS3Files(null);
  };

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    const minSizeKB = 10;
    const minSizeBytes = minSizeKB * 1024;
    const validFiles = [];
    const errors = [];

    selectedFiles.forEach(file => {
      if (file.size < minSizeBytes) {
        errors.push(`${file.name}: zu klein (mind. ${minSizeKB}KB)`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      setSnackbar({
        open: true,
        message: `Fehler: ${errors.join(', ')}`,
        severity: 'error'
      });
    }

    if (validFiles.length > 0) {
      setUploadDialog(prev => ({ ...prev, files: [...prev.files, ...validFiles] }));
    }
  };

  const uploadSingleFile = async (fileToUpload, fileName) => {
    let uploadcareUrl;

    // Convert HEIC to JPG if needed
    if (isHeicFile(fileName)) {
      console.log('🔄 Converting HEIC to JPG...');
      try {
        const convertedFile = await convertHeicToJpg(fileToUpload);
        const originalSizeMB = (fileToUpload.size / (1024 * 1024)).toFixed(2);
        const convertedSizeMB = (convertedFile.size / (1024 * 1024)).toFixed(2);
        console.log('✅ HEIC converted to JPG:', {
          original: originalSizeMB + ' MB',
          converted: convertedSizeMB + ' MB'
        });
        fileToUpload = convertedFile;
        fileName = convertedFile.name;
      } catch (conversionError) {
        console.warn('⚠️ HEIC conversion failed:', conversionError.message);
        throw new Error('HEIC zu JPG Konvertierung fehlgeschlagen');
      }
    }

    // Compress large images to fit under Vercel 5MB limit
    if (fileToUpload.size > 5 * 1024 * 1024) {
      console.log('🗜️ Image is large, compressing...');
      try {
        const canvas = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
              const canvas = document.createElement('canvas');
              // Reduce dimensions by 30% to reduce file size significantly
              canvas.width = img.width * 0.7;
              canvas.height = img.height * 0.7;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              resolve(canvas);
            };
            img.src = e.target.result;
          };
          reader.readAsDataURL(fileToUpload);
        });

        // Convert to blob with 70% quality
        const compressedBlob = await new Promise((resolve) => {
          canvas.toBlob(resolve, 'image/jpeg', 0.7);
        });

        const originalSizeMB = (fileToUpload.size / (1024 * 1024)).toFixed(2);
        const compressedSizeMB = (compressedBlob.size / (1024 * 1024)).toFixed(2);
        const reduction = ((1 - compressedBlob.size / fileToUpload.size) * 100).toFixed(1);

        console.log('✅ Image compressed:', {
          original: originalSizeMB + ' MB',
          compressed: compressedSizeMB + ' MB',
          reduction: reduction + '%'
        });

        // Create new File object from blob
        fileToUpload = new File([compressedBlob], fileName, { type: 'image/jpeg' });
      } catch (compressionError) {
        console.warn('⚠️ Compression failed, using original:', compressionError.message);
        // Continue with original if compression fails
      }
    }

    // Step 1: Upload to ImageKit via backend
    console.log('📤 Uploading to ImageKit:', fileToUpload.name, 'Size:', fileToUpload.size, 'Type:', fileToUpload.type);

    try {
      const formData = new FormData();
      formData.append('file', fileToUpload);

      console.log('📡 Sending file to backend ImageKit upload endpoint...');

      const uploadResponse = await fetch('/api/imagekit/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      console.log('📋 Backend response status:', uploadResponse.status);

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('❌ Backend error response:', errorText);
        throw new Error(`Upload failed with status ${uploadResponse.status}: ${errorText}`);
      }

      const result = await uploadResponse.json();
      uploadcareUrl = result.url;
      const fileId = result.fileId;

      console.log('✅ File uploaded to ImageKit:', uploadcareUrl);
      console.log('📋 ImageKit response:', result);

      // Step 2: Send URL to backend (use URL method)
      console.log('📤 Uploading URL to GMB:', uploadcareUrl, 'Category:', uploadDialog.category);

      const response = await fetch(`/api/businesses/${locationId}/media`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sourceUrl: uploadcareUrl,
          category: uploadDialog.category
        })
      });

      const responseData = await response.json();
      console.log('📥 Upload response:', responseData);

      if (!response.ok) {
        const errorMessage = responseData?.error || responseData?.message || response.statusText;
        throw new Error(errorMessage);
      }

      const createSucceeded = responseData?.createSucceeded !== false;

      // Step 3: Delete file from ImageKit after successful GMB upload
      if (createSucceeded && fileId) {
        console.log('🗑️ Deleting file from ImageKit:', fileId);
        try {
          const deleteResponse = await fetch('/api/imagekit/delete', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ fileId })
          });

          if (deleteResponse.ok) {
            console.log('✅ File deleted successfully from ImageKit');
          } else {
            console.warn('⚠️ Failed to delete file from ImageKit:', await deleteResponse.text());
          }
        } catch (deleteErr) {
          console.warn('⚠️ Error deleting file from ImageKit:', deleteErr.message);
          // Don't throw, as the main upload already succeeded
        }
      }

      return createSucceeded;
    } catch (err) {
      console.error('❌ ImageKit upload failed:', err);
      throw err;
    }
  };

  const handleUpload = async () => {
    if (uploadDialog.method === 'file' && uploadDialog.files.length === 0) return;
    if (uploadDialog.method === 'url' && !uploadDialog.sourceUrl) return;
    if (uploadDialog.method === 's3' && uploadDialog.selectedS3Files.length === 0) return;

    try {
      setUploadDialog(prev => ({ ...prev, uploading: true }));

      if (uploadDialog.method === 'file') {
        // Upload multiple files
        let successCount = 0;
        let failureCount = 0;

        for (const file of uploadDialog.files) {
          try {
            const createSucceeded = await uploadSingleFile(file, file.name);
            if (createSucceeded) {
              successCount++;
            } else {
              failureCount++;
            }
          } catch (err) {
            console.error('Error uploading file:', err);
            failureCount++;
          }
        }

        // Show summary message
        if (successCount > 0) {
          setSnackbar({
            open: true,
            message: `${successCount} Datei${successCount > 1 ? 'en' : ''} erfolgreich hochgeladen! Die Medien werden in Kürze aktualisiert...`,
            severity: 'success'
          });
        }
        if (failureCount > 0) {
          setSnackbar({
            open: true,
            message: `${failureCount} Datei${failureCount > 1 ? 'en' : ''} fehlgeschlagen`,
            severity: 'error'
          });
        }
      } else if (uploadDialog.method === 'url') {
        // URL upload method (direct)
        console.log('📤 Uploading from URL:', uploadDialog.sourceUrl, 'Category:', uploadDialog.category);

        const response = await fetch(`/api/businesses/${locationId}/media`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            sourceUrl: uploadDialog.sourceUrl,
            category: uploadDialog.category
          })
        });

        const responseData = await response.json();
        if (!response.ok) {
          const errorMessage = responseData?.error || responseData?.message || response.statusText;
          throw new Error(errorMessage);
        }

        setSnackbar({
          open: true,
          message: 'URL erfolgreich hochgeladen! Die Medien werden in Kürze aktualisiert...',
          severity: 'success'
        });
      } else if (uploadDialog.method === 's3') {
        // S3 upload method - multiple files
        let successCount = 0;
        let failureCount = 0;

        for (const s3File of uploadDialog.selectedS3Files) {
          try {
            // Construct the full file URL from S3 bucket
            const fileUrl = s3File.url && s3File.key ? `${s3File.url}/${s3File.key}` : s3File.url;
            console.log('📚 Uploading from S3 bucket:', s3File.name, 'Category:', uploadDialog.category);
            console.log('📍 File URL:', fileUrl);

            const response = await fetch(`/api/businesses/${locationId}/media`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                sourceUrl: fileUrl,
                category: uploadDialog.category
              })
            });

            const responseData = await response.json();
            if (!response.ok) {
              const errorMessage = responseData?.error || responseData?.message || response.statusText;
              throw new Error(errorMessage);
            }

            successCount++;
          } catch (err) {
            console.error('Error uploading S3 file:', err);
            failureCount++;
          }
        }

        // Show summary message
        if (successCount > 0) {
          setSnackbar({
            open: true,
            message: `${successCount} Datei${successCount > 1 ? 'en' : ''} aus Cloud-Speicher erfolgreich hochgeladen! Die Medien werden in Kürze aktualisiert...`,
            severity: 'success'
          });
        }
        if (failureCount > 0) {
          setSnackbar({
            open: true,
            message: `${failureCount} Datei${failureCount > 1 ? 'en' : ''} fehlgeschlagen`,
            severity: 'error'
          });
        }
      }

      setUploadDialog({
        open: false,
        files: [],
        sourceUrl: '',
        uploading: false,
        category: 'ADDITIONAL',
        method: 'file',
        s3Files: [],
        s3Loading: false,
        selectedS3Files: [],
        s3CurrentFolder: null,
        s3FolderStack: [],
        s3PreviewFile: null
      });

      // Wait for Google to process and index the media
      console.log('⏳ Waiting for Google to process media...');

      // Try refreshing multiple times with increasing delays
      let mediaFound = false;
      for (let attempt = 1; attempt <= 5; attempt++) {
        const delayMs = attempt === 1 ? 3000 : 2000 * attempt;
        console.log(`⏳ Attempt ${attempt}: Waiting ${delayMs}ms before refresh...`);
        await new Promise(resolve => setTimeout(resolve, delayMs));

        console.log(`🔄 Refreshing media list (attempt ${attempt}/5)...`);
        const checkResponse = await fetch(`/api/media/${locationId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (checkResponse.ok) {
          const checkData = await checkResponse.json();
          const itemCount = checkData.data?.mediaItems?.length || 0;
          console.log(`📊 Media items found: ${itemCount}`);

          if (itemCount > 0) {
            mediaFound = true;
            setMediaData(checkData.data || {});
            console.log(`✅ Media found after attempt ${attempt}!`);
            break;
          }
        }
      }

      if (!mediaFound) {
        console.warn(`⚠️ Media not yet appearing in list. Will be visible shortly.`);
        await fetchMediaData();
      }
    } catch (err) {
      console.error('❌ Upload error:', err);

      // Parse error message for user-friendly feedback
      let errorMessage = `Upload fehlgeschlagen: ${err.message}`;

      if (err.message?.includes('too small') || err.message?.includes('Image too small')) {
        errorMessage = '❌ Bild zu klein: Google Business erfordert Bilder mit mindestens 250×250 Pixeln. Wählen Sie ein größeres Bild.';
      } else if (err.message?.includes('not supported') || err.message?.includes('Image format')) {
        errorMessage = '❌ Bildformat nicht unterstützt. Verwenden Sie JPEG, PNG oder GIF.';
      } else if (err.message?.includes('Invalid argument')) {
        errorMessage = '❌ Ungültiges Bild. Stellen Sie sicher, dass die Datei gültig ist und die Google Business-Anforderungen erfüllt.';
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setUploadDialog(prev => ({ ...prev, uploading: false }));
    }
  };

  const handleDeleteClick = (media) => {
    setDeleteDialog({ open: true, media, deleting: false });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.media) return;

    try {
      setDeleteDialog(prev => ({ ...prev, deleting: true }));
      const mediaName = deleteDialog.media.name;
      const encodedName = encodeURIComponent(mediaName);

      const response = await fetch(
        `/api/businesses/${locationId}/media/${encodedName}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Delete failed: ${response.statusText}`);
      }

      setSnackbar({
        open: true,
        message: 'Datei erfolgreich gelöscht!',
        severity: 'success'
      });
      setDeleteDialog({ open: false, media: null, deleting: false });
      await fetchMediaData();
    } catch (err) {
      console.error('Delete error:', err);
      setSnackbar({
        open: true,
        message: `Fehler beim Löschen: ${err.message}`,
        severity: 'error'
      });
      setDeleteDialog(prev => ({ ...prev, deleting: false }));
    }
  };

  const handleDownload = async (media) => {
    const url = media.proxySourceUrl || media.sourceUrl || media.googleUrl;
    console.log('📥 Download requested for:', media.name?.split('/').pop());
    console.log('🔗 Available URLs:', {
      proxySourceUrl: !!media.proxySourceUrl,
      sourceUrl: !!media.sourceUrl,
      googleUrl: !!media.googleUrl,
      using: url ? (media.proxySourceUrl ? 'proxySourceUrl' : media.sourceUrl ? 'sourceUrl' : 'googleUrl') : 'none'
    });

    if (!url) {
      setSnackbar({
        open: true,
        message: 'Download URL not available for this media',
        severity: 'error'
      });
      return;
    }

    try {
      // For public CDNs (Google, ImageKit), don't send Authorization header
      // For local URLs, send the auth header
      const isPublicCDN = url.includes('lh3.googleusercontent.com') ||
                          url.includes('googleusercontent.com') ||
                          url.includes('imagekit.io') ||
                          url.includes('ik.imagekit.io');

      const fetchOptions = {};
      if (!isPublicCDN) {
        fetchOptions.headers = {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        };
      }

      const response = await fetch(url, fetchOptions);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = media.name?.split('/').pop() || 'media';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      console.log('✅ Download started');
      setSnackbar({
        open: true,
        message: 'Download started successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('❌ Download error:', err);
      setSnackbar({
        open: true,
        message: `Download failed: ${err.message}`,
        severity: 'error'
      });
    }
  };

  const handleCopyLink = (media) => {
    const url = media.proxyThumbnailUrl || media.proxyGoogleUrl || media.googleUrl;
    if (url) {
      navigator.clipboard.writeText(url).then(() => {
        setSnackbar({
          open: true,
          message: 'Link in Zwischenablage kopiert!',
          severity: 'success'
        });
      });
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'PROFILE': 'Profilbild',
      'COVER': 'Titelbild',
      'LOGO': 'Logo',
      'EXTERIOR': 'Außenansicht',
      'INTERIOR': 'Innenansicht',
      'PRODUCT': 'Produkt',
      'AT_WORK': 'Bei der Arbeit',
      'FOOD_AND_DRINK': 'Essen & Trinken',
      'MENU': 'Menü',
      'COMMON_AREA': 'Gemeinschaftsbereich',
      'ROOMS': 'Räume',
      'TEAMS': 'Team',
      'ADDITIONAL': 'Zusätzlich'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'PROFILE': '#2196f3',
      'COVER': '#9c27b0',
      'LOGO': '#f44336',
      'EXTERIOR': '#4caf50',
      'INTERIOR': '#ff9800',
      'PRODUCT': '#00bcd4',
      'AT_WORK': '#3f51b5',
      'FOOD_AND_DRINK': '#ff5722',
      'MENU': '#795548',
      'TEAMS': '#e91e63',
      'ADDITIONAL': '#607d8b'
    };
    return colors[category] || '#9e9e9e';
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" sx={{ fontSize: '2rem', mb: 2, width: '300px' }} />
          <Skeleton variant="text" sx={{ fontSize: '1rem', mb: 3, width: '200px' }} />
        </Box>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} md={3} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" sx={{ fontSize: '1.5rem', mb: 2 }} />
                  <Skeleton variant="rectangular" width="100%" height={60} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={2}>
          {[...Array(12)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Card>
                <Skeleton variant="rectangular" height={200} />
                <CardContent>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <LinearProgress sx={{ borderRadius: 1, height: 6 }} />
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert
          severity="error"
          sx={{ borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchMediaData}>
              <RefreshIcon sx={{ mr: 1 }} />
              Erneut versuchen
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  // Render folder structure tree (recursive, handles deep nesting)
  const renderFolderTree = () => {
    if (!folderStructure?.folders) return null;

    const getStatusColor = (status) => {
      const colors = {
        'uploaded': '#4caf50',
        'pending': '#ff9800',
        'new': '#2196f3',
        'approved': '#8bc34a',
        'rejected': '#f44336'
      };
      return colors[status?.toLowerCase()] || '#9e9e9e';
    };

    // File type from the stored cloud key (e.g. ".../library/abc.jpg"), falling back to the name
    const getFileType = (image) => {
      const source = image.cloudKey || image.name || '';
      const match = source.match(/\.([a-z0-9]+)$/i);
      return match ? match[1].toUpperCase() : '—';
    };

    // Recursive renderer for nested folder structure
    const renderFolders = (folders, prefix = '') => {
      if (!folders || folders.length === 0) return null;

      return folders.map((folder, idx) => {
        const isLast = idx === folders.length - 1;
        const folderPrefix = prefix + (isLast ? '└── ' : '├── ');
        const childPrefix = prefix + (isLast ? '    ' : '│   ');

        const imageCount = folder.images?.length || 0;
        const hasSubfolders = folder.folders && folder.folders.length > 0;
        const hasItems = imageCount > 0 || hasSubfolders;

        return (
          <Box key={`${folder.id}-${idx}`}>
            {/* Folder header */}
            <Typography sx={{ color: '#1976d2', fontWeight: 600, fontSize: '0.9rem', mb: 0.5 }}>
              {folderPrefix}{folder.folderName}/ ({imageCount})
            </Typography>

            {/* Images in folder */}
            {imageCount > 0 && (
              <Box sx={{ mb: 0.5 }}>
                {folder.images.map((image, imgIdx) => {
                  const isLastImg = imgIdx === imageCount - 1;
                  const imgPrefix = childPrefix + (isLastImg && !hasSubfolders ? '└── ' : '├── ');

                  return (
                    <Box key={image._id} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Typography sx={{ color: '#999', fontSize: '0.85rem', minWidth: 'fit-content', mr: 0.5 }}>
                        {imgPrefix}
                      </Typography>
                      <Typography sx={{ color: '#333', fontSize: '0.85rem', flex: 1, minWidth: 0 }}>
                        {image.name}
                      </Typography>
                      <Typography sx={{ color: '#666', fontSize: '0.75rem', fontWeight: 600, ml: 1, flexShrink: 0, minWidth: 48, textAlign: 'right' }}>
                        {getFileType(image)}
                      </Typography>
                      <Chip
                        label={image.status || 'new'}
                        size="small"
                        sx={{
                          ml: 1,
                          height: 20,
                          fontSize: '0.65rem',
                          backgroundColor: getStatusColor(image.status),
                          color: 'white',
                          fontWeight: 600,
                          flexShrink: 0
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Nested subfolders */}
            {hasSubfolders && (
              <Box sx={{ mb: 0.5 }}>
                {renderFolders(folder.folders, childPrefix)}
              </Box>
            )}
          </Box>
        );
      });
    };

    // Count total images recursively
    function countAllImages(folders) {
      let count = 0;
      for (const folder of folders) {
        count += folder.images?.length || 0;
        if (folder.folders?.length > 0) {
          count += countAllImages(folder.folders);
        }
      }
      return count;
    }

    const totalFolders = folderStructure.folders?.length || 0;
    const totalImages = countAllImages(folderStructure.folders) || 0;

    return (
      <Box sx={{ maxHeight: '70vh', overflow: 'auto', bgcolor: 'white', p: 2, fontFamily: 'monospace' }}>
        {/* Header */}
        <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 2 }}>
          {totalFolders} folders · {totalImages} files
        </Typography>

        {/* Render all folders recursively */}
        {renderFolders(folderStructure.folders)}
      </Box>
    );
  };

  // Calculate statistics
  const statistics = {
    totalMedia: mediaData?.mediaItems?.length || 0,
    photos: mediaData?.mediaItems?.filter(item => item.mediaFormat === 'PHOTO').length || 0,
    videos: mediaData?.mediaItems?.filter(item => item.mediaFormat === 'VIDEO').length || 0,
    profilePhotos: mediaData?.mediaItems?.filter(
      item => item.locationAssociation?.category === 'PROFILE'
    ).length || 0
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Paper
        elevation={1}
        sx={{
          p: 3,
          mb: 4,
          backgroundColor: 'white',
          border: '1px solid #E0E0E0',
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography
              variant="h3"
              gutterBottom
              sx={{ fontWeight: 700, mb: 1, color: '#000000' }}
            >
              Medien-Management
            </Typography>
            <Typography variant="h6" sx={{ color: '#605D5C', fontWeight: 400 }}>
              {selectedBusiness?.businessName || 'Ihr Unternehmen'}
            </Typography>
            {tenantId && (
              <Typography variant="caption" sx={{ color: '#959595', display: 'block', mt: 0.5, fontFamily: 'monospace' }}>
                Tenant ID: <strong>{tenantId}</strong>
              </Typography>
            )}
            <Typography variant="body2" sx={{ color: '#959595', mt: 1 }}>
              Verwalten Sie alle Bilder und Videos zentral
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={handleUploadClick}
              sx={{
                backgroundColor: '#C41E3A',
                '&:hover': { backgroundColor: '#A01729' },
                borderRadius: 2,
                px: 3
              }}
            >
              Hochladen
            </Button>
            <Button
              variant="outlined"
              startIcon={<NotificationIcon />}
              onClick={handleOwnerNotification}
              sx={{
                borderColor: '#FF6F00',
                color: '#FF6F00',
                '&:hover': { bgcolor: 'rgba(255, 111, 0, 0.1)' },
                borderRadius: 2,
                px: 3
              }}
            >
              Besitzer benachrichtigen
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchMediaData}
              sx={{
                borderRadius: 2,
                px: 3
              }}
            >
              Aktualisieren
            </Button>
            <Button
              variant="outlined"
              startIcon={<GraphIcon />}
              onClick={async () => {
                await fetchFreshFolderStructure();
                setFolderStructureOpen(true);
              }}
              sx={{
                borderColor: '#2196F3',
                color: '#2196F3',
                '&:hover': { bgcolor: 'rgba(33, 150, 243, 0.1)' },
                borderRadius: 2,
                px: 3
              }}
              disabled={!folderStructure}
            >
              Graph
            </Button>
          </Stack>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3}>
          {[
            { label: 'Gesamt', value: statistics.totalMedia },
            { label: 'Fotos', value: statistics.photos },
            { label: 'Videos', value: statistics.videos },
            { label: 'Profilbilder', value: statistics.profilePhotos, highlight: true }
          ].map((stat, i) => (
            <Grid item xs={6} md={3} key={i}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  backgroundColor: '#FAFAFA',
                  border: stat.highlight ? '2px solid #FF9800' : '1px solid #E0E0E0',
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mb: 0.5,
                    color: stat.highlight ? '#FF9800' : '#000000'
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="body2" sx={{ color: '#605D5C', fontWeight: 500 }}>
                  {stat.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Medien durchsuchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
              }}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Kategorie</InputLabel>
              <Select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} label="Kategorie">
                <MenuItem value="all">Alle Kategorien</MenuItem>
                <MenuItem value="PROFILE">Profilbild</MenuItem>
                <MenuItem value="COVER">Titelbild</MenuItem>
                <MenuItem value="LOGO">Logo</MenuItem>
                <MenuItem value="EXTERIOR">Außenansicht</MenuItem>
                <MenuItem value="INTERIOR">Innenansicht</MenuItem>
                <MenuItem value="PRODUCT">Produkt</MenuItem>
                <MenuItem value="AT_WORK">Bei der Arbeit</MenuItem>
                <MenuItem value="FOOD_AND_DRINK">Essen & Trinken</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Typ</InputLabel>
              <Select value={filterType} onChange={(e) => setFilterType(e.target.value)} label="Typ">
                <MenuItem value="all">Alle Typen</MenuItem>
                <MenuItem value="PHOTO">Fotos</MenuItem>
                <MenuItem value="VIDEO">Videos</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sortierung</InputLabel>
              <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} label="Sortierung">
                <MenuItem value="newest">Neueste zuerst</MenuItem>
                <MenuItem value="oldest">Älteste zuerst</MenuItem>
                <MenuItem value="name">Name</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Chip label={`${filteredMedia.length} Ergebnisse`} color="primary" variant="outlined" />
          </Grid>
        </Grid>
      </Paper>

      {/* Media Grid */}
      {filteredMedia.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Keine Medien gefunden. Versuchen Sie, die Filter anzupassen oder neue Medien hochzuladen.
        </Alert>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {paginatedMedia.map((media) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={media.name}>
                <MediaCard
                  media={media}
                  onPreview={handlePreview}
                  onDownload={handleDownload}
                  onCopyLink={handleCopyLink}
                  onDelete={handleDeleteClick}
                  getCategoryLabel={getCategoryLabel}
                  getCategoryColor={getCategoryColor}
                  businessData={businessData}
                  locationId={locationId}
                  metadataMap={metadataMap}
                />
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
              <Paper elevation={1} sx={{ borderRadius: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}>
                  <Button disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
                    Zurück
                  </Button>

                  {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                    let page;
                    if (totalPages <= 7) page = i + 1;
                    else if (currentPage <= 4) page = i + 1;
                    else if (currentPage > totalPages - 4) page = totalPages - 6 + i;
                    else page = currentPage - 3 + i;

                    if (page < 1 || page > totalPages) return null;

                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? 'contained' : 'text'}
                        onClick={() => handlePageChange(page)}
                        sx={{
                          minWidth: 40,
                          bgcolor: currentPage === page ? '#C41E3A' : 'transparent',
                          color: currentPage === page ? 'white' : 'text.primary',
                          '&:hover': {
                            bgcolor: currentPage === page ? '#a01729' : 'rgba(0,0,0,0.04)'
                          }
                        }}
                      >
                        {page}
                      </Button>
                    );
                  }).filter(Boolean)}

                  <Button disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                    Weiter
                  </Button>
                </Stack>
              </Paper>
            </Box>
          )}
        </>
      )}

      {/* Preview Modal */}
      <PreviewModal
        open={previewDialog.open}
        media={previewDialog.media}
        onClose={() => setPreviewDialog({ open: false, media: null })}
        onDownload={handleDownload}
        onDelete={handleDeleteClick}
        onCopyLink={handleCopyLink}
        getCategoryLabel={getCategoryLabel}
      />

      {/* Upload Dialog */}
      <UploadDialog
        open={uploadDialog.open}
        files={uploadDialog.files}
        sourceUrl={uploadDialog.sourceUrl}
        uploading={uploadDialog.uploading}
        category={uploadDialog.category}
        method={uploadDialog.method}
        s3Files={uploadDialog.s3Files}
        s3Loading={uploadDialog.s3Loading}
        selectedS3Files={uploadDialog.selectedS3Files}
        s3CurrentFolder={uploadDialog.s3CurrentFolder}
        s3FolderStack={uploadDialog.s3FolderStack}
        s3PreviewFile={uploadDialog.s3PreviewFile}
        metadataMap={metadataMap}
        locationId={locationId}
        onUpdateFileMetadata={updateFileMetadata}
        onFileSelect={handleFileSelect}
        onSourceUrlChange={(url) => setUploadDialog({ ...uploadDialog, sourceUrl: url })}
        onMethodChange={(method) => {
          if (method === 's3') {
            console.log('🔄 Fetching fresh mediaMapping and S3 files...');
            fetchFreshFolderStructure();
            fetchMetadataMap();
            handleS3GoToRoot();
            fetchS3Files(null);
          }
          setUploadDialog({ ...uploadDialog, method, files: [], sourceUrl: '', selectedS3Files: [] });
        }}
        onRemoveFile={(index) => setUploadDialog(prev => ({ ...prev, files: prev.files.filter((_, i) => i !== index) }))}
        onCategoryChange={(category) => setUploadDialog({ ...uploadDialog, category })}
        onAddS3File={(file) => {
          // Check if file already selected
          if (!uploadDialog.selectedS3Files.find(f => f._id === file._id)) {
            setUploadDialog(prev => ({ ...prev, selectedS3Files: [...prev.selectedS3Files, file] }));
          }
        }}
        onRemoveS3File={(fileId) => setUploadDialog(prev => ({
          ...prev,
          selectedS3Files: prev.selectedS3Files.filter(f => f._id !== fileId)
        }))}
        onPreviewS3File={(file) => setUploadDialog(prev => ({ ...prev, s3PreviewFile: file }))}
        onS3FolderOpen={handleS3FolderOpen}
        onS3FolderBack={handleS3FolderBack}
        onS3GoToRoot={handleS3GoToRoot}
        onUpload={handleUpload}
        onCancel={() => setUploadDialog({
          open: false,
          files: [],
          sourceUrl: '',
          uploading: false,
          category: 'ADDITIONAL',
          method: 'file',
          s3Files: [],
          s3Loading: false,
          selectedS3Files: [],
          s3CurrentFolder: null,
          s3FolderStack: [],
          s3PreviewFile: null
        })}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialog.open}
        media={deleteDialog.media}
        deleting={deleteDialog.deleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog({ open: false, media: null, deleting: false })}
      />

      {/* Owner Notification Dialog */}
      <Dialog open={ownerNotificationOpen} onClose={() => setOwnerNotificationOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <NotificationIcon />
            <Typography variant="h6">Besitzer benachrichtigen</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3}>
            {/* Recipient Section */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Empfänger
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={ownerNotificationRecipient}
                onChange={(e) => setOwnerNotificationRecipient(e.target.value)}
                placeholder="E-Mail Adresse eingeben"
                type="email"
                helperText="E-Mail wird an diese Adresse versendet"
              />
            </Box>

            {/* Subject Section */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Betreff
              </Typography>
              <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2">
                  Bitte lade neue Bilder für dein Google-Unternehmensprofil hoch
                </Typography>
              </Paper>
            </Box>

            {/* Email Content Section */}
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                E-Mail Inhalt
              </Typography>
              <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, maxHeight: 400, overflow: 'auto' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6, fontSize: '0.9rem' }}>
                  {ownerNotificationEmailContent}
                </Typography>
              </Paper>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOwnerNotificationOpen(false)}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            onClick={handleSendOwnerNotification}
            disabled={ownerNotificationSending || !ownerNotificationRecipient.trim()}
            startIcon={ownerNotificationSending ? <CircularProgress size={16} /> : <SendIcon />}
            sx={{ backgroundColor: '#FF6F00', '&:hover': { backgroundColor: '#E65100' } }}
          >
            {ownerNotificationSending ? 'Sende...' : `E-Mail an ${ownerNotificationRecipient} senden`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Folder Structure Modal */}
      <Dialog open={folderStructureOpen} onClose={() => setFolderStructureOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <GraphIcon />
            <Typography variant="h6">Ordnerstruktur - {tenantId}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {renderFolderTree()}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setFolderStructureOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Container>
  );
};

// Media Card Component
const MediaCard = ({
  media,
  onPreview,
  onDownload,
  onCopyLink,
  onDelete,
  getCategoryLabel,
  getCategoryColor,
  businessData,
  locationId,
  metadataMap
}) => {
  const isVideo = media.mediaFormat === 'VIDEO';
  // Prefer proxyThumbnailUrl which should have CORS headers, fall back to thumbnailUrl
  let thumbnailUrl = media.proxyThumbnailUrl || media.thumbnailUrl;
  const [imageError, setImageError] = useState(false);
  const [extractedVideoThumb, setExtractedVideoThumb] = useState(null);
  const [isExtractingThumb, setIsExtractingThumb] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editValues, setEditValues] = useState({ category: '', status: '' });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Use extracted thumbnail if available
  if (extractedVideoThumb) {
    thumbnailUrl = extractedVideoThumb;
  }

  // Extract video thumbnail if missing
  useEffect(() => {
    // For videos, always try to extract even if thumbnailUrl exists (it might not be accessible)
    if (!isVideo) {
      console.log(`ℹ️ Not a video: ${media.name?.split('/').pop()}`);
      return;
    }

    if (extractedVideoThumb) {
      console.log(`ℹ️ Already have extracted thumbnail for: ${media.name?.split('/').pop()}`);
      return;
    }

    if (isExtractingThumb) {
      console.log(`ℹ️ Currently extracting for: ${media.name?.split('/').pop()}`);
      return;
    }

    const videoSourceUrl = media.proxySourceUrl || media.sourceUrl || media.googleUrl;
    if (!videoSourceUrl) {
      console.warn(`⚠️ No source URL for video: ${media.name?.split('/').pop()}`, {
        hasProxySourceUrl: !!media.proxySourceUrl,
        hasSourceUrl: !!media.sourceUrl,
        hasGoogleUrl: !!media.googleUrl,
        allKeys: Object.keys(media).filter(k => k.includes('url') || k.includes('Url') || k.includes('URL'))
      });
      return;
    }

    const extractThumb = async () => {
      setIsExtractingThumb(true);
      try {
        console.log(`🎬 Extracting thumbnail for video: ${media.name?.split('/').pop()}`, { videoSourceUrl: videoSourceUrl.substring(0, 80) + '...' });
        const response = await fetch('/api/extract-video-thumbnail', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ fileUrl: videoSourceUrl })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `HTTP ${response.status}`);
        }

        const result = await response.json();
        if (result.success && result.thumbnailUrl) {
          console.log(`✅ Video thumbnail extracted and set: ${media.name?.split('/').pop()}`);
          setExtractedVideoThumb(result.thumbnailUrl);
          setImageError(false); // Reset error flag since we have a good thumbnail now
        } else {
          console.warn(`⚠️ No thumbnailUrl in response for: ${media.name?.split('/').pop()}`, result);
        }
      } catch (err) {
        console.warn(`⚠️ Failed to extract video thumbnail: ${err.message}`, { videoName: media.name?.split('/').pop() });
        // Don't show error, just use placeholder
      } finally {
        setIsExtractingThumb(false);
      }
    };

    extractThumb();
  }, [media._id]);

  // Get image metadata from metadataMap by _id
  const imageMetadata = useMemo(() => {
    // First, try to match by reference fields in media object
    const mediaSourceId = media.sourceId || media.referenceId || media.s3Id || media.cloudStorageId;

    // If Google media has a reference to S3 _id, use it
    if (mediaSourceId && metadataMap && metadataMap[mediaSourceId]) {
      console.log(`✅ Found metadata by sourceId for ${media.name?.split('/').pop()}:`, metadataMap[mediaSourceId]);
      return metadataMap[mediaSourceId];
    }

    // Fallback to businessData mediaMapping
    if (businessData?.mediaMapping?.folders && businessData.mediaMapping.folders.length > 0) {
      for (const folder of businessData.mediaMapping.folders) {
        if (folder.images && folder.images.length > 0) {
          const sampleImage = folder.images[0];
          if (sampleImage.category || sampleImage.status) {
            return {
              category: sampleImage.category,
              status: sampleImage.status,
              folder: folder.folderName,
              source: 'mediaMapping'
            };
          }
        }
      }
    }

    // Final fallback to defaults
    console.log(`⚠️ Using defaults for ${media.name?.split('/').pop()}`);
    return { category: 'Produkt', status: 'new', source: 'default' };
  }, [businessData, metadataMap, media]);

  console.log(`🖼️ Media: ${media.name?.split('/').pop()}`, {
    hasProxyUrl: !!media.proxyThumbnailUrl,
    hasThumbnailUrl: !!media.thumbnailUrl,
    using: media.proxyThumbnailUrl ? 'proxyThumbnailUrl' : 'thumbnailUrl'
  });

  // Debug logging for videos
  if (isVideo) {
    console.log(`🎥 Video media debug: ${media.name?.split('/').pop()}`, {
      hasProxySourceUrl: !!media.proxySourceUrl,
      hasSourceUrl: !!media.sourceUrl,
      hasGoogleUrl: !!media.googleUrl,
      hasThumbnailUrl: !!thumbnailUrl,
      using: thumbnailUrl ? (media.proxyThumbnailUrl ? 'proxyThumbnailUrl' : 'thumbnailUrl') : 'none',
      allFields: Object.keys(media)
    });
  }

  // Use placeholder if no thumbnail URL, or if image failed and we're not extracting a replacement
  const shouldShowPlaceholder = !thumbnailUrl || (imageError && !isExtractingThumb && !extractedVideoThumb);

  // For videos, show play icon in placeholder
  const renderVideoPlaceholder = () => (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: isVideo ? '#2a2a2a' : 'grey.300'
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        {isVideo ? (
          <>
            <PlayIcon sx={{ fontSize: 56, color: 'white', mb: 1 }} />
            <Typography variant="caption" color="white">
              {isExtractingThumb ? 'Lade...' : 'Video'}
            </Typography>
          </>
        ) : (
          <>
            <ImageIcon sx={{ fontSize: 48, color: 'grey.500', mb: 1 }} />
            <Typography variant="caption" color="textSecondary">
              {!thumbnailUrl ? 'No thumbnail' : `Loading...` }
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );

  const handleEditClick = () => {
    setEditValues({
      category: imageMetadata?.category || '',
      status: imageMetadata?.status || ''
    });
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditValues({ category: '', status: '' });
  };

  const handleConfirmUpdate = () => {
    setConfirmDialogOpen(true);
  };

  const handleUpdateClick = async () => {
    setConfirmDialogOpen(false);
    setIsUpdating(true);

    try {
      const updates = {};
      if (editValues.category) updates.category = editValues.category;
      if (editValues.status) updates.status = editValues.status;

      const token = localStorage.getItem('auth_token');
      const mediaSourceId = media.sourceId || media.referenceId || media.s3Id || media.cloudStorageId;

      if (mediaSourceId && Object.keys(updates).length > 0) {
        const response = await fetch(`/api/folders-with-metadata/${locationId}/${mediaSourceId}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updates)
        });

        if (response.ok) {
          console.log(`✅ Updated file metadata:`, updates);
          setIsEditMode(false);
          // Trigger a refresh by updating the metadataMap locally
          window.location.reload();
        } else {
          throw new Error('Failed to update metadata');
        }
      }
    } catch (err) {
      console.error('Error updating metadata:', err);
      alert(`Fehler beim Aktualisieren: ${err.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        border: 1,
        borderColor: 'divider',
        position: 'relative',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-2px)',
          cursor: 'pointer'
        }
      }}
    >
      {/* Category Badge */}
      {media.locationAssociation?.category && (
        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
          <Chip
            label={getCategoryLabel(media.locationAssociation.category)}
            size="small"
            sx={{
              bgcolor: getCategoryColor(media.locationAssociation.category),
              color: 'white',
              fontWeight: 600
            }}
          />
        </Box>
      )}

      {/* Image/Video Thumbnail */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          paddingTop: '100%',
          backgroundColor: 'grey.200',
          overflow: 'hidden'
        }}
      >
        {shouldShowPlaceholder ? (
          renderVideoPlaceholder()
        ) : (
          <img
            key={extractedVideoThumb ? 'extracted' : 'original'}
            src={thumbnailUrl}
            alt={media.name}
              crossOrigin="anonymous"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            onLoad={() => {
              console.log(`✅ Image loaded: ${media.name?.split('/').pop()}`);
            }}
            onError={(e) => {
              console.warn(`⚠️ Image load error for ${media.name?.split('/').pop()}`);
              // Don't set error if we have an extracted thumbnail (dataUrl)
              if (!extractedVideoThumb) {
                setImageError(true);
              }
            }}
          />
        )}

        {/* Video Play Icon - Show on all videos */}
        {isVideo && !shouldShowPlaceholder && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'rgba(0,0,0,0.7)',
              borderRadius: '50%',
              width: 56,
              height: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2
            }}
          >
            <PlayIcon sx={{ fontSize: 32, color: 'white' }} />
          </Box>
        )}
      </Box>

      <CardContent sx={{ p: 2 }}>

        {/* Metadata */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="caption" color="textSecondary">
            {media.mediaFormat || 'PHOTO'}
          </Typography>
          {media.dimensions && (
            <Typography variant="caption" color="textSecondary">
              {media.dimensions.widthPixels}×{media.dimensions.heightPixels}
            </Typography>
          )}
        </Box>

        {/* Date */}
        {media.createTime && (
          <Typography variant="caption" color="textSecondary">
            {format(parseISO(media.createTime), 'dd.MM.yyyy', { locale: de })}
          </Typography>
        )}
      </CardContent>

      {/* Action Buttons */}
      <Box sx={{ p: 2, pt: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Ansehen">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onPreview(media);
              }}
            >
              <PreviewIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Herunterladen">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDownload(media);
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Link kopieren">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onCopyLink(media);
              }}
            >
              <CopyIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {

        <Tooltip title="Löschen">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(media);
            }}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>


        }
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm">
        <DialogTitle>Änderungen bestätigen?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Die Metadaten werden direkt in der Datenbank aktualisiert:
          </Typography>
          <Box sx={{ pl: 2 }}>
            {editValues.category && (
              <Typography variant="body2" color="textSecondary">
                • Kategorie: <strong>{editValues.category}</strong>
              </Typography>
            )}
            {editValues.status && (
              <Typography variant="body2" color="textSecondary">
                • Status: <strong>{editValues.status.split(' ')[0]}</strong>
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleUpdateClick}
            disabled={isUpdating}
            sx={{ backgroundColor: '#C41E3A', '&:hover': { backgroundColor: '#A01729' } }}
          >
            {isUpdating ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {isUpdating ? 'Wird aktualisiert...' : 'Bestätigen und Aktualisieren'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

// Preview Modal Component
const PreviewModal = ({ open, media, onClose, onDownload, onDelete, onCopyLink, getCategoryLabel }) => {
  const [videoError, setVideoError] = useState(false);

  if (!media) return null;

  const isVideo = media.mediaFormat === 'VIDEO';
  const mediaUrl = media.proxySourceUrl || media.sourceUrl || media.googleUrl;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Medienvorschau</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {mediaUrl ? (
          isVideo ? (
            videoError ? (
              <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'grey.100', borderRadius: 1 }}>
                <PlayIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                <Typography color="textSecondary" sx={{ mb: 2 }}>
                  Video-Vorschau ist nicht verfügbar
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Das Video kann heruntergeladen werden, um es anzuschauen.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#000' }}>
                <video
                  src={mediaUrl}
                  controls
                  style={{ width: '100%', maxHeight: '600px', objectFit: 'contain' }}
                  onError={() => {
                    console.warn(`⚠️ Video playback error for: ${media.name}`);
                    setVideoError(true);
                  }}
                />
              </Box>
            )
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#f5f5f5', minHeight: '400px' }}>
              <img
                src={mediaUrl}
                alt={media.name}
                  crossOrigin="anonymous"
                style={{ width: '100%', maxHeight: '600px', objectFit: 'contain' }}
              />
            </Box>
          )
        ) : (
          <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'grey.100', borderRadius: 1 }}>
            {isVideo ? (
              <>
                <PlayIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                <Typography color="textSecondary" sx={{ mb: 2 }}>
                  Video-Vorschau nicht verfügbar
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Die Video-URL konnte nicht geladen werden. Bitte versuchen Sie, das Video herunterzuladen.
                </Typography>
              </>
            ) : (
              <>
                <ImageIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                <Typography color="textSecondary">Medienvorschau nicht verfügbar</Typography>
              </>
            )}
          </Box>
        )}

        {/* Metadata */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Informationen
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Name</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {media.name?.split('/').pop() || 'Unnamed'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Typ</Typography>
              <Typography variant="body2">{media.mediaFormat}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Kategorie</Typography>
              <Typography variant="body2">
                {media.locationAssociation?.category ? getCategoryLabel(media.locationAssociation.category) : 'Keine'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Größe</Typography>
              <Typography variant="body2">
                {media.dimensions
                  ? `${media.dimensions.widthPixels}×${media.dimensions.heightPixels}`
                  : 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">Erstellt</Typography>
              <Typography variant="body2">
                {media.createTime
                  ? format(parseISO(media.createTime), 'dd.MM.yyyy HH:mm', { locale: de })
                  : 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<DownloadIcon />}
          onClick={() => {
            onDownload(media);
            onClose();
          }}
        >
          Herunterladen
        </Button>
        <Button
          startIcon={<CopyIcon />}
          onClick={() => {
            onCopyLink(media);
            onClose();
          }}
        >
          Link kopieren
        </Button>

        {/* 
        <Button
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => {
            onDelete(media);
            onClose();
          }}
        >
          Löschen
        </Button>

        */}
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

// Upload Dialog Component
const UploadDialog = ({
  open,
  files,
  sourceUrl,
  uploading,
  category,
  method,
  s3Files,
  s3Loading,
  selectedS3Files,
  s3CurrentFolder,
  s3FolderStack,
  s3PreviewFile,
  metadataMap,
  locationId,
  onUpdateFileMetadata,
  onFileSelect,
  onSourceUrlChange,
  onMethodChange,
  onRemoveFile,
  onCategoryChange,
  onAddS3File,
  onRemoveS3File,
  onPreviewS3File,
  onS3FolderOpen,
  onS3FolderBack,
  onS3GoToRoot,
  onUpload,
  onCancel
}) => {
  const [editingFileId, setEditingFileId] = useState(null);
  const [editValues, setEditValues] = useState({ category: '', status: '' });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [heicPreviewUrls, setHeicPreviewUrls] = useState({});
  const [heicLoadingStates, setHeicLoadingStates] = useState({});
  const [videoPreviewUrls, setVideoPreviewUrls] = useState({});
  const [videoLoadingStates, setVideoLoadingStates] = useState({});
  const [videoPlayingId, setVideoPlayingId] = useState(null);

  // Category mapping
  const categoryMap = {
    'PROFILE': 'Profilbild',
    'COVER': 'Titelbild',
    'LOGO': 'Logo',
    'EXTERIOR': 'Außenansicht',
    'INTERIOR': 'Innenansicht',
    'PRODUCT': 'Produkt',
    'AT_WORK': 'Bei der Arbeit',
    'FOOD_AND_DRINK': 'Essen & Trinken',
    'MENU': 'Menü',
    'ADDITIONAL': 'Zusätzlich'
  };

  const labelToValueMap = Object.fromEntries(
    Object.entries(categoryMap).map(([k, v]) => [v, k])
  );

  const getCategoryValue = (labelOrValue) => {
    return labelToValueMap[labelOrValue] || labelOrValue;
  };

  // Convert HEIC files in S3 to preview URLs via backend
  useEffect(() => {
    const convertHeicFiles = async () => {
      const newUrls = { ...heicPreviewUrls };
      const newLoadingStates = { ...heicLoadingStates };

      for (const item of s3Files || []) {
        if (isHeicFile(item.name, item.details?.extension)) {
          const itemId = item._id || item.id;
          if (!newUrls[itemId] && item.url && item.key) {
            newLoadingStates[itemId] = true;
            try {
              const fileUrl = `${item.url}/${item.key}`;
              console.log(`🔄 Converting HEIC preview for ${item.name} via backend...`);

              const response = await fetch('/api/convert-heic-preview', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ fileUrl })
              });

              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `HTTP ${response.status}`);
              }

              const result = await response.json();
              if (result.success && result.dataUrl) {
                console.log(`✅ HEIC preview converted for ${item.name}`);
                newUrls[itemId] = result.dataUrl;
              } else {
                throw new Error('No data URL in response');
              }
            } catch (err) {
              console.warn(`❌ Failed to convert HEIC preview for ${item.name}:`, err.message);
            } finally {
              newLoadingStates[itemId] = false;
            }
          }
        }
      }

      setHeicPreviewUrls(newUrls);
      setHeicLoadingStates(newLoadingStates);
    };

    if (s3Files && s3Files.length > 0) {
      convertHeicFiles();
    }
  }, [s3Files]);

  // Convert HEIC when preview dialog opens
  useEffect(() => {
    const convertPreviewHeic = async () => {
      if (!s3PreviewFile || !isHeicFile(s3PreviewFile.name, s3PreviewFile.details?.extension)) {
        return;
      }

      const itemId = s3PreviewFile._id || s3PreviewFile.id;
      const existingUrl = heicPreviewUrls[itemId];
      const isLoading = heicLoadingStates[itemId];

      if (existingUrl || isLoading) return;

      if (!s3PreviewFile.url || !s3PreviewFile.key) {
        console.warn('⚠️ HEIC file missing url or key:', s3PreviewFile);
        return;
      }

      const newLoadingStates = { ...heicLoadingStates };
      newLoadingStates[itemId] = true;
      setHeicLoadingStates(newLoadingStates);

      try {
        const fileUrl = `${s3PreviewFile.url}/${s3PreviewFile.key}`;
        console.log(`🔄 Converting HEIC preview (from preview dialog)...`);

        const response = await fetch('/api/convert-heic-preview', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ fileUrl })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `HTTP ${response.status}`);
        }

        const result = await response.json();
        if (result.success && result.dataUrl) {
          console.log(`✅ HEIC preview converted (preview dialog)`);
          const newUrls = { ...heicPreviewUrls };
          newUrls[itemId] = result.dataUrl;
          setHeicPreviewUrls(newUrls);
        }
      } catch (err) {
        console.warn(`❌ Failed to convert HEIC in preview:`, err.message);
      } finally {
        newLoadingStates[itemId] = false;
        setHeicLoadingStates(newLoadingStates);
      }
    };

    convertPreviewHeic();
  }, [s3PreviewFile]);

  // Extract video thumbnails for all videos in file list
  useEffect(() => {
    const extractVideoThumbnails = async () => {
      if (!s3Files || s3Files.length === 0) return;

      const newUrls = { ...videoPreviewUrls };
      const newLoadingStates = { ...videoLoadingStates };
      let hasChanges = false;

      for (const item of s3Files) {
        if (item.isFolder) continue;

        const itemId = item._id || item.id;
        const fileKey = item.key || item.path || item.filePath || item.details?.key;
        const isVideo = fileKey?.toLowerCase().match(/\.(mov|mp4|avi|mkv|webm)$/i);

        if (!isVideo) continue;

        // Skip if already extracted or loading
        if (newUrls[itemId] || newLoadingStates[itemId]) continue;

        const fileUrl = item.url || item.fileUrl || item.downloadUrl || item.s3Url || item.details?.url;
        if (!fileUrl || !fileKey) continue;

        const fullFileUrl = fileUrl.endsWith('/') || fileKey.startsWith('/')
          ? `${fileUrl}${fileKey}`
          : `${fileUrl}/${fileKey}`;

        newLoadingStates[itemId] = true;
        hasChanges = true;

        try {
          console.log(`🎬 Extracting thumbnail for ${item.name}...`);
          const response = await fetch('/api/extract-video-thumbnail', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ fileUrl: fullFileUrl })
          });

          if (response.ok) {
            const result = await response.json();
            if (result.success && result.thumbnailUrl) {
              console.log(`✅ Thumbnail extracted for ${item.name} (${result.thumbnailUrl})`);
              newUrls[itemId] = result.thumbnailUrl;
            } else {
              console.warn(`⚠️ No thumbnailUrl in response for ${item.name}:`, result);
            }
          } else {
            const errorData = await response.json().catch(() => ({}));
            console.warn(`⚠️ HTTP ${response.status} for ${item.name}: ${errorData.message || 'Unknown error'}`);
          }
        } catch (err) {
          console.warn(`⚠️ Failed to extract thumbnail for ${item.name}: ${err.message}`);
        } finally {
          newLoadingStates[itemId] = false;
        }
      }

      if (hasChanges) {
        console.log(`📊 Setting ${Object.keys(newUrls).length} video thumbnails to state`, Object.entries(newUrls).map(([k, v]) => `${k}: ${v?.length || 0}b`));
        setVideoPreviewUrls(newUrls);
        setVideoLoadingStates(newLoadingStates);
      }
    };

    extractVideoThumbnails();
  }, [s3Files]);

  // Extract video thumbnail when preview opens
  useEffect(() => {
    const extractVideoPreviewThumb = async () => {
      if (!s3PreviewFile) return;

      // Check if it's a video
      const fileKey = s3PreviewFile.key || s3PreviewFile.path || s3PreviewFile.filePath || s3PreviewFile.details?.key;
      const isVideo = fileKey?.toLowerCase().match(/\.(mov|mp4|avi|mkv|webm)$/i);

      if (!isVideo) return;

      const itemId = s3PreviewFile._id || s3PreviewFile.id;
      const existingUrl = videoPreviewUrls[itemId];
      const isLoading = videoLoadingStates[itemId];

      if (existingUrl || isLoading) return;

      // Build the complete S3 URL
      const fileUrl = s3PreviewFile.url || s3PreviewFile.fileUrl || s3PreviewFile.downloadUrl || s3PreviewFile.s3Url || s3PreviewFile.details?.url;
      if (!fileUrl || !fileKey) {
        console.warn('⚠️ Video file missing url or key:', s3PreviewFile);
        return;
      }

      const fullFileUrl = fileUrl.endsWith('/') || fileKey.startsWith('/')
        ? `${fileUrl}${fileKey}`
        : `${fileUrl}/${fileKey}`;

      const newLoadingStates = { ...videoLoadingStates };
      newLoadingStates[itemId] = true;
      setVideoLoadingStates(newLoadingStates);

      try {
        console.log(`🎬 Extracting video thumbnail for ${s3PreviewFile.name}...`);
        console.log(`📍 Full file URL: ${fullFileUrl}`);

        const response = await fetch('/api/extract-video-thumbnail', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ fileUrl: fullFileUrl })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `HTTP ${response.status}`);
        }

        const result = await response.json();
        if (result.success && result.thumbnailUrl) {
          console.log(`✅ Video thumbnail extracted for ${s3PreviewFile.name} (${result.thumbnailUrl})`);
          const newUrls = { ...videoPreviewUrls };
          newUrls[itemId] = result.thumbnailUrl;
          setVideoPreviewUrls(newUrls);
        } else {
          console.warn(`⚠️ No thumbnailUrl in response for ${s3PreviewFile.name}:`, result);
        }
      } catch (err) {
        console.warn(`⚠️ Failed to extract video thumbnail: ${err.message}`);
      } finally {
        newLoadingStates[itemId] = false;
        setVideoLoadingStates(newLoadingStates);
      }
    };

    extractVideoPreviewThumb();
  }, [s3PreviewFile]);

  const categories = [
    { value: 'PROFILE', label: 'Profilbild' },
    { value: 'COVER', label: 'Titelbild' },
    { value: 'LOGO', label: 'Logo' },
    { value: 'EXTERIOR', label: 'Außenansicht' },
    { value: 'INTERIOR', label: 'Innenansicht' },
    { value: 'PRODUCT', label: 'Produkt' },
    { value: 'AT_WORK', label: 'Bei der Arbeit' },
    { value: 'FOOD_AND_DRINK', label: 'Essen & Trinken' },
    { value: 'MENU', label: 'Menü' },
    { value: 'ADDITIONAL', label: 'Zusätzlich' }
  ];

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xl" fullWidth PaperProps={{ sx: { height: '90vh', maxHeight: '90vh' } }}>
      <DialogTitle>Medien hochladen</DialogTitle>
      <DialogContent dividers sx={{ overflow: 'auto' }}>
        {/* Method selector */}
        <FormControl fullWidth size="small" sx={{ mb: 3 }}>
          <InputLabel>Upload-Methode</InputLabel>
          <Select
            value={method}
            onChange={(e) => onMethodChange(e.target.value)}
            label="Upload-Methode"
          >
            <MenuItem value="file">
              <UploadIcon sx={{ mr: 1, fontSize: 20 }} />
              Datei hochladen
            </MenuItem>
            <MenuItem value="url">
              <PhotoLibraryIcon sx={{ mr: 1, fontSize: 20 }} />
              URL eingeben
            </MenuItem>
            <MenuItem value="s3">
              <CloudDownloadIcon sx={{ mr: 1, fontSize: 20 }} />
              Aus Cloud-Speicher auswählen
            </MenuItem>
          </Select>
        </FormControl>

        {/* File upload method */}
        {method === 'file' && (
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': { borderColor: '#C41E3A', bgcolor: 'rgba(196, 30, 58, 0.05)' }
              }}
            >
              <input
                type="file"
                id="media-upload"
                hidden
                multiple
                onChange={onFileSelect}
                accept=".jpg,.jpeg,.png,.gif,.heic,.heif,.mp4,.mov,.webm"
              />
              <label htmlFor="media-upload" style={{ cursor: 'pointer', display: 'block' }}>
                <UploadIcon sx={{ fontSize: 48, color: '#C41E3A', mb: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {files.length > 0 ? `${files.length} Datei${files.length > 1 ? 'en' : ''} ausgewählt` : 'Dateien zum Hochladen auswählen'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  oder Dateien hierher ziehen
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                  Maximal 20 MB pro Datei (JPEG, PNG, GIF, HEIC, MP4)
                </Typography>
                <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 0.5 }}>
                  HEIC-Dateien werden automatisch zu JPEG konvertiert
                </Typography>
              </label>
            </Box>

            {/* Display selected files */}
            {files.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Ausgewählte Dateien:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {files.map((file, index) => {
                    const isHeic = isHeicFile(file.name);
                    return (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1,
                          bgcolor: isHeic ? 'rgba(33, 150, 243, 0.08)' : 'rgba(196, 30, 58, 0.08)',
                          borderRadius: 1,
                          border: isHeic ? '1px solid rgba(33, 150, 243, 0.2)' : '1px solid rgba(196, 30, 58, 0.2)'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: 1, flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <ImageIcon sx={{ fontSize: 20, color: isHeic ? '#2196f3' : '#C41E3A', flexShrink: 0 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                {file.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, ml: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Chip
                              label={`Kategorie: ${category}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 24 }}
                            />
                          </Box>
                          {isHeic && (
                            <Chip
                              label="wird zu JPEG konvertiert"
                              size="small"
                              variant="outlined"
                              sx={{ ml: 4, height: 24, fontSize: '0.7rem', color: '#2196f3', borderColor: '#2196f3' }}
                            />
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => onRemoveFile(index)}
                          sx={{ color: isHeic ? '#2196f3' : '#C41E3A' }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* URL upload method */}
        {method === 'url' && (
          <TextField
            fullWidth
            label="Bild-URL"
            placeholder="https://example.com/image.jpg"
            value={sourceUrl}
            onChange={(e) => onSourceUrlChange(e.target.value)}
            size="small"
            sx={{ mb: 3 }}
            helperText="Geben Sie die vollständige URL zu dem Bild oder Video ein"
          />
        )}

        {/* S3 upload method */}
        {method === 's3' && (
          <Box sx={{ mb: 3 }}>
            {/* Breadcrumb Navigation - Always show if in a folder */}
            {s3CurrentFolder && (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  onClick={onS3FolderBack}
                  variant="outlined"
                >
                  Zurück
                </Button>
                <Button
                  size="small"
                  onClick={onS3GoToRoot}
                  variant="text"
                >
                  Zur Bibliothek
                </Button>
              </Box>
            )}

            {s3Loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={40} />
                <Typography sx={{ ml: 2 }}>Cloud-Speicher wird geladen...</Typography>
              </Box>
            ) : !Array.isArray(s3Files) || s3Files.length === 0 ? (
              <Alert severity="info">Keine Dateien im Cloud-Speicher gefunden</Alert>
            ) : (
              <Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  {s3CurrentFolder ? 'Ordnerinhalte:' : 'Dateien und Ordner:'}
                </Typography>
                <Box sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  maxHeight: '500px',
                  overflow: 'auto'
                }}>
                  {Array.isArray(s3Files) && s3Files.map((item) => {
                    const itemId = item._id || item.id;
                    const itemName = item.name || item.fileName || 'Untitled';
                    const isFolder = item.isFolder;
                    const fileType = item.mediaFormat || item.type || 'FILE';
                    const fileKey = item.key || item.path || item.filePath || item.details?.key || item.name || '';
                    const isVideoByExtension = fileKey?.toLowerCase().match(/\.(mov|mp4|avi|mkv|webm)$/i);
                    const isVideo = fileType === 'VIDEO' || !!isVideoByExtension;
                    const itemDate = item.createdAt || item.created_at || item.uploadedAt;
                    const isSelected = selectedS3Files.some(f => f._id === itemId);

                    if (isFolder) {
                      return (
                        <Box
                          key={itemId}
                          sx={{
                            p: 2,
                            borderBottom: '1px solid #e0e0e0',
                            cursor: 'pointer',
                            backgroundColor: 'rgba(255, 152, 0, 0.05)',
                            transition: 'all 0.2s',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 152, 0, 0.15)'
                            }
                          }}
                          onClick={() => onS3FolderOpen(item)}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <FolderIcon sx={{ color: '#FF9800' }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                                📁 {itemName}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                Ordner
                              </Typography>
                            </Box>
                            <ArrowBackIcon sx={{ color: '#FF9800', transform: 'rotate(180deg)' }} />
                          </Box>
                        </Box>
                      );
                    }

                    return (
                      <Box
                        key={itemId}
                        sx={{
                          p: 2,
                          borderBottom: '1px solid #e0e0e0',
                          backgroundColor: isSelected ? 'rgba(196, 30, 58, 0.1)' : 'transparent',
                          borderLeft: isSelected ? '4px solid #C41E3A' : '4px solid transparent',
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: 'rgba(196, 30, 58, 0.05)'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, width: '100%' }}>
                          {/* Thumbnail Preview */}
                          <Box
                            sx={{
                              width: 150,
                              height: 150,
                              flexShrink: 0,
                              backgroundColor: '#f5f5f5',
                              borderRadius: 1,
                              overflow: 'hidden',
                              border: '1px solid #e0e0e0',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            onClick={() => onPreviewS3File(item)}
                          >
                            {isVideo ? (
                              // Video thumbnail
                              videoPreviewUrls[itemId] ? (
                                <img
                                  key={`thumb-${itemId}-${videoPreviewUrls[itemId]}`}
                                  src={videoPreviewUrls[itemId]}
                                  alt={itemName}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    position: 'relative'
                                  }}
                                  onError={(e) => {
                                    const srcStr = e.target.src || 'undefined';
                                    console.warn(`Video thumbnail load error for ${itemName}`);
                                    console.warn(`  - src: ${srcStr.substring(0, 150)}`);
                                    console.warn(`  - videoPreviewUrls[${itemId}]: ${videoPreviewUrls[itemId]?.substring(0, 150) || 'undefined'}`);
                                  }}
                                />
                              ) : videoLoadingStates[itemId] ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                                  <CircularProgress size={24} />
                                </Box>
                              ) : (
                                <Box sx={{ textAlign: 'center', position: 'relative' }}>
                                  <PlayIcon sx={{ fontSize: 40, color: '#C41E3A' }} />
                                </Box>
                              )
                            ) : item.url && item.key ? (
                              isHeicFile(item.name, item.details?.extension) ? (
                                heicPreviewUrls[itemId] ? (
                                  <Box
                                    component="img"
                                    src={heicPreviewUrls[itemId]}
                                    alt={itemName}
                                    sx={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover'
                                    }}
                                    onError={(e) => {
                                      console.warn(`Image load error for HEIC ${itemName}`);
                                    }}
                                  />
                                ) : heicLoadingStates[itemId] ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                                    <CircularProgress size={24} />
                                  </Box>
                                ) : (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                                    <ImageIcon sx={{ fontSize: 40, color: '#2196f3', mb: 0.5 }} />
                                    <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#2196f3' }}>HEIC</Typography>
                                  </Box>
                                )
                              ) : (
                                // Use image proxy for regular images
                                <Box
                                  component="img"
                                  src={`/api/s3-image-proxy?fileKey=${encodeURIComponent(item.key)}${item.url ? '&fileUrl=' + encodeURIComponent(item.url + '/' + item.key) : ''}`}
                                  alt={itemName}
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                  onError={(e) => {
                                    console.warn(`Image load error for ${itemName}`);
                                    // Replace with placeholder on error
                                    e.target.style.display = 'none';
                                    const parent = e.target.parentElement;
                                    if (parent && !parent.querySelector('[data-placeholder]')) {
                                      const placeholder = document.createElement('div');
                                      placeholder.setAttribute('data-placeholder', 'true');
                                      placeholder.style.cssText = 'width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: #f5f5f5;';
                                      placeholder.innerHTML = '<svg style="width: 40px; height: 40px; color: #ccc;" fill="currentColor" viewBox="0 0 24 24"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>';
                                      parent.appendChild(placeholder);
                                    }
                                  }}
                                />
                              )
                            ) : (
                              <ImageIcon sx={{ fontSize: 40, color: '#999' }} />
                            )}
                          </Box>

                          {/* File Info */}
                          <Box sx={{ flex: 1, cursor: 'pointer' }} onClick={() => onPreviewS3File(item)}>
                            <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word', mb: 0.25 }}>
                              {itemName}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.75 }}>
                              <Typography variant="caption" sx={{ color: '#C41E3A', fontWeight: 600 }}>
                                Type: {(() => {
                                  // Helper to clean extension (remove leading dot)
                                  const cleanExt = (ext) => {
                                    if (!ext) return null;
                                    const cleaned = ext.replace(/^\./, '').toUpperCase();
                                    return cleaned && cleaned.length < 10 && /^[A-Z0-9]+$/i.test(cleaned) ? cleaned : null;
                                  };

                                  // Check if HEIC
                                  if (isHeicFile(item.name, item.details?.extension)) return 'HEIC';

                                  // Trust mediaFormat or type if it's VIDEO or specific format
                                  if (fileType && fileType !== 'FILE') return fileType;

                                  // Check item.details.extension (Contact Cloud API)
                                  const detailsExt = cleanExt(item.details?.extension);
                                  if (detailsExt) return detailsExt;

                                  // Check item.extension
                                  const itemExt = cleanExt(item.extension);
                                  if (itemExt) return itemExt;

                                  // Extract from S3 key (Contact Cloud file path)
                                  const keyPath = item.key || item.path || '';
                                  const keyExt = cleanExt(keyPath?.split('.')?.pop());
                                  if (keyExt) return keyExt;

                                  // Extract from filename
                                  const nameExt = cleanExt(itemName?.split('.')?.pop());
                                  if (nameExt && itemName?.includes('.')) return nameExt;

                                  // Fallback
                                  return 'FILE';
                                })()}
                              </Typography>
                              {itemDate && (
                                <Typography variant="caption" color="textSecondary">
                                  {new Date(itemDate).toLocaleDateString('de-DE')}
                                </Typography>
                              )}
                            </Box>

                            {/* Status and Category Tags from metadataMap */}
                            {metadataMap && metadataMap[itemId] && (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {metadataMap[itemId].category && (
                                  <Chip
                                    label={metadataMap[itemId].category}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontSize: '0.65rem',
                                      backgroundColor: '#E3F2FD',
                                      color: '#1976D2',
                                      fontWeight: 600
                                    }}
                                  />
                                )}
                                {metadataMap[itemId].status && (
                                  <Chip
                                    label={metadataMap[itemId].status}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontSize: '0.65rem',
                                      backgroundColor: metadataMap[itemId].status === 'new' ? '#F3E5F5' :
                                                        metadataMap[itemId].status === 'rejected' ? '#FFCDD2' :
                                                        metadataMap[itemId].status === 'approved' ? '#C8E6C9' :
                                                        metadataMap[itemId].status === 'uploaded' ? '#BBDEFB' : '#F3E5F5',
                                      color: metadataMap[itemId].status === 'new' ? '#7B1FA2' :
                                             metadataMap[itemId].status === 'rejected' ? '#B71C1C' :
                                             metadataMap[itemId].status === 'approved' ? '#1B5E20' :
                                             metadataMap[itemId].status === 'uploaded' ? '#0D47A1' : '#7B1FA2',
                                      fontWeight: 600
                                    }}
                                  />
                                )}
                              </Box>
                            )}
                          </Box>

                          {/* Action Buttons */}
                          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon sx={{ fontSize: '0.875rem' }} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingFileId(itemId);
                                setEditValues({
                                  category: getCategoryValue(metadataMap?.[itemId]?.category) || '',
                                  status: metadataMap?.[itemId]?.status || ''
                                });
                              }}
                              sx={{
                                color: '#C41E3A',
                                borderColor: '#C41E3A',
                                fontSize: '0.75rem',
                                textTransform: 'none',
                                fontWeight: 600
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              variant={isSelected ? 'contained' : 'outlined'}
                              onClick={() => {
                                if (isSelected) {
                                  onRemoveS3File(itemId);
                                } else {
                                  onAddS3File(item);
                                }
                              }}
                              sx={{
                                backgroundColor: isSelected ? '#C41E3A' : 'transparent',
                                color: isSelected ? 'white' : '#C41E3A',
                                borderColor: '#C41E3A',
                                fontSize: '0.75rem',
                                textTransform: 'none',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              {isSelected ? '✓' : '+'}
                              {!isSelected && 'Add'}
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {selectedS3Files.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {selectedS3Files.length} Datei{selectedS3Files.length > 1 ? 'en' : ''} ausgewählt
                    </Alert>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Ausgewählte Dateien:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {selectedS3Files.map((file) => {
                        const fileMetadata = metadataMap?.[file._id];
                        const isHeic = isHeicFile(file.name, file.details?.extension);
                        console.log(`📋 File ${file.name} (_id: ${file._id}):`, {
                          hasMetadataMap: !!metadataMap,
                          metadataKeys: metadataMap ? Object.keys(metadataMap).slice(0, 3) : 'N/A',
                          fileMetadata,
                          isHeic
                        });
                        return (
                          <Box
                            key={file._id}
                            sx={{
                              p: 1.5,
                              bgcolor: isHeic ? 'rgba(33, 150, 243, 0.08)' : 'rgba(196, 30, 58, 0.08)',
                              borderRadius: 1,
                              border: isHeic ? '1px solid rgba(33, 150, 243, 0.2)' : '1px solid rgba(196, 30, 58, 0.2)',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1.5
                            }}
                          >
                            {/* Icon */}
                            <ImageIcon sx={{ fontSize: 20, color: isHeic ? '#2196f3' : '#C41E3A', flexShrink: 0 }} />

                            {/* File Info */}
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ wordBreak: 'break-word', fontWeight: 500 }}>
                                {file.name}
                              </Typography>
                            </Box>

                            {/* Remove Button */}
                            <Button
                              size="small"
                              onClick={() => onRemoveS3File(file._id)}
                              sx={{ color: isHeic ? '#2196f3' : '#C41E3A', minWidth: 'auto', p: 0.5 }}
                            >
                              <CloseIcon fontSize="small" />
                            </Button>

                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Category selector */}
        <FormControl fullWidth size="small">
          <InputLabel>Kategorie</InputLabel>
          <Select
            value={category}
            onChange={(e) => onCategoryChange(e.target.value)}
            label="Kategorie"
          >
            {categories.map(cat => (
              <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Abbrechen</Button>
        <Button
          variant="contained"
          onClick={onUpload}
          disabled={
            (method === 'file' && files.length === 0) ||
            (method === 'url' && !sourceUrl) ||
            (method === 's3' && selectedS3Files.length === 0) ||
            uploading
          }
          sx={{ backgroundColor: '#C41E3A', '&:hover': { backgroundColor: '#A01729' } }}
        >
          {uploading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          {uploading ? 'Wird hochgeladen...' : 'Hochladen'}
        </Button>
      </DialogActions>

      {/* S3 File Preview Dialog */}
      {s3PreviewFile && (
        <Dialog
          open={!!s3PreviewFile}
          onClose={() => onPreviewS3File(null)}
          maxWidth="sm"
          fullWidth
        >
          {console.log('📸 Preview file ALL properties:', {
            ...s3PreviewFile,
            keys: Object.keys(s3PreviewFile),
            stringified: JSON.stringify(s3PreviewFile, null, 2)
          })}
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Dateivorschau</Typography>
            <IconButton onClick={() => onPreviewS3File(null)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ textAlign: 'center' }}>
              {(() => {
                // Get file URL from multiple possible properties
                const fileUrl = s3PreviewFile.url || s3PreviewFile.fileUrl || s3PreviewFile.downloadUrl || s3PreviewFile.s3Url || s3PreviewFile.details?.url;
                const fileKey = s3PreviewFile.key || s3PreviewFile.path || s3PreviewFile.filePath || s3PreviewFile.details?.key;
                const hasUrl = fileUrl && fileKey;

                console.log('🖼️ Preview URL check:', { fileUrl, fileKey, hasUrl, details: s3PreviewFile.details, allProps: s3PreviewFile });

                if (!hasUrl) {
                  return <Typography color="textSecondary">Vorschau nicht verfügbar</Typography>;
                }

                // Check if it's a video file
                const isVideo = fileKey?.toLowerCase().match(/\.(mov|mp4|avi|mkv|webm)$/i);

                if (isVideo) {
                  const itemId = s3PreviewFile._id || s3PreviewFile.id;
                  const videoThumb = videoPreviewUrls[itemId];
                  const isLoadingThumb = videoLoadingStates[itemId];
                  const isPlaying = videoPlayingId === itemId;

                  // Build video URL using proxy endpoint for authentication
                  const videoUrl = (() => {
                    const key = s3PreviewFile.key || s3PreviewFile.path || s3PreviewFile.filePath || s3PreviewFile.details?.key;
                    if (!key) return null;
                    // Use the s3-video-proxy endpoint which handles AWS authentication
                    const url = fileUrl ? encodeURIComponent(fileUrl + '/' + key) : null;
                    return `/api/s3-video-proxy?fileKey=${encodeURIComponent(key)}${url ? '&fileUrl=' + url : ''}`;
                  })();

                  if (isPlaying && videoUrl) {
                    return (
                      <Box sx={{ position: 'relative', width: '100%', mb: 2 }}>
                        <video
                          src={videoUrl}
                          controls
                          autoPlay
                          style={{
                            width: '100%',
                            maxHeight: '400px',
                            objectFit: 'contain',
                            borderRadius: '8px',
                            backgroundColor: '#000'
                          }}
                          onError={(e) => {
                            console.warn(`⚠️ Video playback error`);
                            setVideoPlayingId(null);
                          }}
                        />
                      </Box>
                    );
                  }

                  if (videoThumb) {
                    return (
                      <Box
                        sx={{
                          position: 'relative',
                          width: '100%',
                          maxHeight: '400px',
                          mb: 2,
                          cursor: 'pointer',
                          borderRadius: 1,
                          overflow: 'hidden'
                        }}
                        onClick={() => setVideoPlayingId(itemId)}
                      >
                        <Box
                          component="img"
                          src={videoThumb}
                          alt={s3PreviewFile.name}
                          sx={{
                            width: '100%',
                            maxHeight: '400px',
                            objectFit: 'contain',
                            display: 'block'
                          }}
                          onError={(e) => {
                            console.warn(`⚠️ Video thumbnail load error`);
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            bgcolor: 'rgba(0,0,0,0.6)',
                            borderRadius: '50%',
                            width: 80,
                            height: 80,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              bgcolor: 'rgba(196, 30, 58, 0.8)',
                              transform: 'translate(-50%, -50%) scale(1.1)'
                            }
                          }}
                        >
                          <PlayIcon sx={{ fontSize: 48, color: 'white' }} />
                        </Box>
                      </Box>
                    );
                  }

                  if (isLoadingThumb) {
                    return (
                      <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <CircularProgress sx={{ mb: 2 }} />
                        <Typography color="textSecondary">Video-Vorschau wird generiert...</Typography>
                      </Box>
                    );
                  }

                  return (
                    <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <PlayIcon sx={{ fontSize: 80, color: '#C41E3A', mb: 2 }} />
                      <Typography color="textSecondary" sx={{ mb: 2 }}>
                        Videovorschau nicht verfügbar
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Die Video-Vorschau konnte nicht generiert werden.
                      </Typography>
                    </Box>
                  );
                }

                if (isHeicFile(s3PreviewFile.name, s3PreviewFile.details?.extension)) {
                  return heicPreviewUrls[s3PreviewFile._id] ? (
                    <Box
                      component="img"
                      src={heicPreviewUrls[s3PreviewFile._id]}
                      alt={s3PreviewFile.name}
                      sx={{
                        width: '100%',
                        maxHeight: '400px',
                        objectFit: 'contain',
                        borderRadius: 1,
                        mb: 2
                      }}
                      onError={(e) => {
                        console.warn(`Preview image load error for HEIC`);
                      }}
                    />
                  ) : heicLoadingStates[s3PreviewFile._id] ? (
                    <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <CircularProgress sx={{ mb: 2 }} />
                      <Typography color="textSecondary">HEIC wird konvertiert...</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <ImageIcon sx={{ fontSize: 64, color: '#2196f3', mb: 1 }} />
                      <Typography color="textSecondary">HEIC-Vorschau wird vorbereitet...</Typography>
                    </Box>
                  );
                }

                return (
                  <Box
                    component="img"
                    src={`/api/s3-image-proxy?fileKey=${encodeURIComponent(fileKey)}${fileUrl ? '&fileUrl=' + encodeURIComponent(fileUrl + '/' + fileKey) : ''}`}
                    alt={s3PreviewFile.name}
                    sx={{
                      width: '100%',
                      maxHeight: '400px',
                      objectFit: 'contain',
                      borderRadius: 1,
                      mb: 2
                    }}
                    onError={(e) => {
                      console.warn(`⚠️ Image load error for ${s3PreviewFile.name}`);
                      e.target.style.display = 'none';
                      const parent = e.target.parentElement;
                      if (parent && !parent.querySelector('[data-error-placeholder]')) {
                        const placeholder = document.createElement('div');
                        placeholder.setAttribute('data-error-placeholder', 'true');
                        placeholder.style.cssText = 'width: 100%; height: 200px; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #f5f5f5; border-radius: 8px; color: #999;';
                        placeholder.innerHTML = '<svg style="width: 48px; height: 48px; margin-bottom: 8px; color: #ccc;" fill="currentColor" viewBox="0 0 24 24"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg><p style="margin: 0; font-size: 12px;">Vorschau nicht verfügbar</p>';
                        parent.appendChild(placeholder);
                      }
                    }}
                  />
                );
              })()}
              <Box sx={{ mt: 2, textAlign: 'left' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Dateiinformationen
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Name:</strong> {s3PreviewFile.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Typ:</strong> {(() => {
                    // Helper to clean extension
                    const cleanExt = (ext) => {
                      if (!ext) return null;
                      const cleaned = ext.replace(/^\./, '').toUpperCase();
                      return cleaned && cleaned.length < 10 && /^[A-Z0-9]+$/i.test(cleaned) ? cleaned : null;
                    };

                    // Check if HEIC
                    if (isHeicFile(s3PreviewFile.name, s3PreviewFile.details?.extension)) return 'HEIC';

                    // Trust mediaFormat/type if it's VIDEO or other specific format
                    if (s3PreviewFile.mediaFormat && s3PreviewFile.mediaFormat !== 'FILE') return s3PreviewFile.mediaFormat;
                    if (s3PreviewFile.type && s3PreviewFile.type !== 'FILE') return s3PreviewFile.type;

                    // Check extensions
                    const detailsExt = cleanExt(s3PreviewFile.details?.extension);
                    if (detailsExt) return detailsExt;

                    const itemExt = cleanExt(s3PreviewFile.extension);
                    if (itemExt) return itemExt;

                    // Extract from S3 key
                    const keyPath = s3PreviewFile.key || s3PreviewFile.path || '';
                    const keyExt = cleanExt(keyPath?.split('.')?.pop());
                    if (keyExt) return keyExt;

                    // Extract from filename
                    const nameExt = cleanExt(s3PreviewFile.name?.split('.')?.pop());
                    if (nameExt && s3PreviewFile.name?.includes('.')) return nameExt;

                    return 'FILE';
                  })()}
                </Typography>
                {s3PreviewFile.createdAt && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>Erstellt:</strong> {new Date(s3PreviewFile.createdAt).toLocaleDateString('de-DE')}
                  </Typography>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => onPreviewS3File(null)}>Schließen</Button>
            <Button
              variant="contained"
              onClick={() => {
                onAddS3File(s3PreviewFile);
                onPreviewS3File(null);
              }}
              sx={{ backgroundColor: '#C41E3A', '&:hover': { backgroundColor: '#A01729' } }}
            >
              + Hinzufügen
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Edit File Metadata Dialog */}
      <Dialog open={!!editingFileId} onClose={() => setEditingFileId(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Status und Kategorie bearbeiten</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Kategorie</InputLabel>
            <Select
              value={editValues.category || ''}
              onChange={(e) => setEditValues({ ...editValues, category: e.target.value })}
              label="Kategorie"
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: { maxWidth: '400px' }
                }
              }}
            >
              <MenuItem value="PROFILE">Profilbild</MenuItem>
              <MenuItem value="COVER">Titelbild</MenuItem>
              <MenuItem value="LOGO">Logo</MenuItem>
              <MenuItem value="EXTERIOR">Außenansicht</MenuItem>
              <MenuItem value="INTERIOR">Innenansicht</MenuItem>
              <MenuItem value="PRODUCT">Produkt</MenuItem>
              <MenuItem value="AT_WORK">Bei der Arbeit</MenuItem>
              <MenuItem value="FOOD_AND_DRINK">Essen & Trinken</MenuItem>
              <MenuItem value="MENU">Menü</MenuItem>
              <MenuItem value="ADDITIONAL">Zusätzlich</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={editValues.status}
              onChange={(e) => setEditValues({ ...editValues, status: e.target.value })}
              label="Status"
              MenuProps={{
                PaperProps: {
                  sx: { maxWidth: '400px' }
                }
              }}
            >
              <MenuItem value="new">new</MenuItem>
              <MenuItem value="approved">approved</MenuItem>
              <MenuItem value="rejected">rejected</MenuItem>
              <MenuItem value="uploaded">uploaded</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditingFileId(null)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={() => setConfirmDialogOpen(true)}
            sx={{ backgroundColor: '#C41E3A', '&:hover': { backgroundColor: '#A01729' } }}
          >
            Aktualisieren
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm">
        <DialogTitle>Änderungen bestätigen?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Die Metadaten werden direkt in der Datenbank aktualisiert:
          </Typography>
          <Box sx={{ pl: 2 }}>
            {editValues.category && (
              <Typography variant="body2" color="textSecondary">
                • Kategorie: <strong>{editValues.category}</strong>
              </Typography>
            )}
            {editValues.status && (
              <Typography variant="body2" color="textSecondary">
                • Status: <strong>{editValues.status.split(' ')[0]}</strong>
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (editingFileId) {
                onUpdateFileMetadata(editingFileId, {
                  category: categoryMap[editValues.category] || editValues.category,
                  status: editValues.status
                });
              }
              setConfirmDialogOpen(false);
              setEditingFileId(null);
            }}
            sx={{ backgroundColor: '#C41E3A', '&:hover': { backgroundColor: '#A01729' } }}
          >
            Bestätigen und Aktualisieren
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

// Delete Confirmation Dialog Component
const DeleteConfirmDialog = ({ open, media, deleting, onConfirm, onCancel }) => {
  if (!media) return null;

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>Datei löschen?</DialogTitle>
      <DialogContent>
        
        <Typography>
          Sind Sie sicher, dass Sie diese Datei löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Abbrechen</Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={deleting}
        >
          {deleting ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          {deleting ? 'Wird gelöscht...' : 'Löschen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MediaManager;
